import { pipe, map, reduce, split, join } from 'rambda';
import {
  membersToFlatExtractors,
  extractHttpMethodFromOperationType,
} from './extractors';
import { getArtifactId, getName } from '../../treeVisitors/helpers';
import { TYPES } from './consts';
import { getSortOrder } from '../../applyMenuConfigChanges/addSortOrderProperty/sortBySortOrder';
import { removeMarkdownFromText } from '../../markdownUtils';
import { toUrlFormat } from '../../treeVisitors';
import { isArticlesSectionGuide } from '../../typeUtils';

const getKey = (key) => key.split('.service.').shift();
const removeSymbol = (symbol) => (str) => str.replace(symbol, '');

export const buildKey = pipe(getKey, removeSymbol('/'));

export const transformNodeKeyToDocsUrl = (key = '') => {
  return key.replace(/\./g, '/');
};

const withoutLast = (arr) => {
  if (arr.length > 1) {
    return arr.slice(0, arr.length - 1);
  }
  return arr;
};

const emptyArrayIfOneElement = (arr) => (arr.length === 1 ? [] : arr);

export const getParentKeyFromCurrentNodeKey = pipe(
  split('.'),
  emptyArrayIfOneElement,
  withoutLast,
  join('.'),
);

export const getTextFromDesc = (desc) => {
  return typeof desc === 'string' ? desc : desc.join(' ');
};

const toIndexedProperties =
  (nodeKey) =>
  ({ data = { docs: {} }, type }) => ({
    type,
    name: getName(data),
    description: data?.docs?.description
      ? removeMarkdownFromText(getTextFromDesc(data?.docs?.description))
      : '',
    summary: removeMarkdownFromText(data?.docs?.summary),
    id: nodeKey,
    originalPath: data.originalPath,
    urlFormat: data.urlFormat,
    sortOrder: getSortOrder(data),
    parentRef: getParentKeyFromCurrentNodeKey(nodeKey),
    link: transformNodeKeyToDocsUrl(nodeKey),
    artifactId: getArtifactId(data),
  });

export const toFlatObjectMembers = (nodeKey) =>
  pipe(
    (node) => ({
      data: node.data,
      type:
        node.type || (node.data && node.data.memberOf)
          ? TYPES.SECTION
          : TYPES.CATEGORY,
    }),
    toIndexedProperties(nodeKey),
  );

const toNormalizedForm = (prefix, normalizeBy) => (accm, obj) => {
  const id = !prefix ? `${obj[normalizeBy]}` : `${prefix}.${obj[normalizeBy]}`;
  return {
    ...accm,
    [id]: { ...obj },
  };
};

const toIndexedPropertiesMdGuides = (data) => {
  const { nodeKey, url, isExternalLink } = data;
  return {
    link: isExternalLink ? url : transformNodeKeyToDocsUrl(nodeKey),
    extra: {
      isArticlesSectionGuide: true,
    },
    ...(isExternalLink ? { isExternalLink } : {}),
  };
};

const toIndexedPropertiesApis = (node, data, type) => {
  const { serviceName, topic, nodeKey } = data;
  return {
    sortOrder: getSortOrder(data),
    link: transformNodeKeyToDocsUrl(nodeKey),
    artifactId: getArtifactId(node.data),
    serviceName,
    topic,
    ...extractHttpMethodFromOperationType(type, data),
  };
};

const getAdditionalPropsByType = (node, data, type) =>
  isArticlesSectionGuide({ data, type })
    ? toIndexedPropertiesMdGuides(data, type)
    : toIndexedPropertiesApis(node, data, type);

const toIndexedPropertiesSubMembers =
  (type, node) =>
  (data = { docs: {} }) => {
    const { docs, urlFormat, nodeKey, mixedInFrom } = data;
    const indexedSubmember = {
      type,
      name: getName(data),
      description: docs?.description
        ? removeMarkdownFromText(getTextFromDesc(docs.description))
        : '',
      summary: removeMarkdownFromText(docs?.summary),
      id: nodeKey,
      originalPath: data.originalPath,
      urlFormat,
      parentRef: getParentKeyFromCurrentNodeKey(nodeKey),
      ...getAdditionalPropsByType(node, data, type),
    };

    if (type === TYPES.MD) {
      // this is done conditionally to not bloat the flatTree with falsy values
      indexedSubmember.guideId = data.id || data.path;
    }

    if (mixedInFrom) {
      // this is done conditionally to not bloat the flatTree with falsy values
      indexedSubmember.originalMixedInFromLink = getLinkToMixedInFrom(
        mixedInFrom.original,
      );
    }

    return indexedSubmember;
  };

export const getLinkToMixedInFrom = (mixedInFrom) =>
  mixedInFrom &&
  toUrlFormat(
    `/${mixedInFrom.memberOf}/${transformNodeKeyToDocsUrl(mixedInFrom.name)}`,
  );

const addNodeKey = (prefix) => (obj) => ({
  nodeKey: !prefix ? `${obj.urlFormat}` : `${prefix}.${obj.urlFormat}`,
  ...obj,
});

const toFlatSubMembers =
  (nodeKey, node) =>
  (flattenSubMembersAccm, [type, extractor]) => ({
    ...flattenSubMembersAccm,
    ...extractAndNormalizeSubMember(nodeKey, node, extractor, type),
  });

const extractAndNormalizeSubMember = (nodeKey, node, extractor, type) =>
  pipe(
    extractor,
    map(pipe(addNodeKey(nodeKey), toIndexedPropertiesSubMembers(type, node))),
    reduce(toNormalizedForm(nodeKey, 'urlFormat'), {}),
  )(node);

export const flattenSubMembers = (nodeKey, node) => {
  return Object.entries(membersToFlatExtractors).reduce(
    toFlatSubMembers(nodeKey, node),
    {},
  );
};
